import { useContext, useState, useEffect, useMemo } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'

import { Typography, Box, Button, Tooltip, IconButton, InputAdornment, TextField, RadioGroup, Radio, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButtonGroup, ToggleButton, MenuItem, Alert } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { FileUploader } from "react-drag-drop-files"
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import _ from 'lodash'
import { HexColorPicker } from 'react-colorful'
import isEmail from 'validator/lib/isEmail'
import isURL from 'validator/lib/isURL'
import { useTheme } from '@mui/material/styles'

import { CopyToClipboard } from 'react-copy-to-clipboard'
import { enqueueSnackbar } from 'notistack'
import QRCode from "react-qr-code"
import BwipJs from "@bwip-js/browser"
import moment from 'moment-timezone'

import SaveAltIcon from '@mui/icons-material/SaveAlt'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'

import { AppContext } from '/providers/app-context'
import { createTestStampcard, createTestLoyaltycard, createGiftcard } from '/providers/studio-api'
import { getTemplateData } from '/providers/templates-api'
import { makePass } from '/providers/passes-api'

import PageWrapper from '/components/page-wrapper'
import StyledPaper from '/components/paper'
import Menu from '/components/templates/template-nav-menu'
import batchSendMakePasses from '/components/make-passes/batch-send-make-passes'
import parseUploadedData from '/components/make-passes/parse-uploaded-data'
import ResultsGrid from '/components/make-passes/results-grid'
import DataExportDialog from '/components/data-export-dialog'
import excelExport from '/components/make-passes/sample-data-to-excel'
import csvExport from '/components/make-passes/sample-data-to-csv'
import ActionErrorDialog from '../components/action-error-dialog'
import ClickToCopy from '/components/click-to-copy'
import getLeafNodes from '/utils/get-leaf-nodes'
import ShortUniqueId from 'short-unique-id'
import currencies from '/utils/currencies.json'
import { isRequiredVariableValueForTemplate } from '/utils/variable-required'

import HelpText from '/help/template-issue-passes'

import appleButton from '/assets/images/add-to-apple-wallet.svg'
import googleButton from '/assets/images/add-to-google-wallet.svg'

const IssuePasses = () => {

  const { loadingTemplates, templates } = useContext(AppContext)
  const { templateId } = useParams()

  const template = useMemo(() => { return templates.filter(t => t.template_id === templateId)[0] }, [templates, templateId])

  if (!loadingTemplates && !template) return <Navigate to="/templates" replace />

  if (template?.pass_type_id === "managed_stamp") return (
    <PageWrapper title="Issue New Stampcard" HelpText={HelpText} heading={`${template?.name} ${template?.archived ? " [archived]" : ""}`} menu={<Menu template={template} />}>
      <div className='overflow-y-scroll h-full'>
        <StampcardSignup template={template} />
      </div>
    </PageWrapper>
  )

  if (template?.pass_type_id === "managed_loyalty") return (
    <PageWrapper title="Issue New Loyalty card" HelpText={HelpText} heading={`${template?.name} ${template?.archived ? " [archived]" : ""}`} menu={<Menu template={template} />}>
      <div className='overflow-y-scroll h-full'>
        <LoyaltycardSignup template={template} HelpText={HelpText} />
      </div>
    </PageWrapper>
  )

  if (template?.pass_type_id === "managed_gift") return (
    <PageWrapper title="Issue New Giftcard" HelpText={HelpText} heading={`${template?.name} ${template?.archived ? " [archived]" : ""}`} menu={<Menu template={template} />}>
      <div className='overflow-y-scroll h-full'>
        <Giftcard template={template} HelpText={HelpText} />
      </div>
    </PageWrapper>
  )

  return (
    <PageWrapper title="Create Passes" HelpText={HelpText} heading={`${template?.name} ${template?.archived ? " [archived]" : ""}`} menu={<Menu template={template} />}>
      <div className='overflow-y-scroll h-full'>
        <Content template={template} />
      </div>
    </PageWrapper>
  )
}

export default IssuePasses


const Content = ({ template }) => {
  const { apiSource, apiKey, processing, setProcessing, user } = useContext(AppContext)
  const theme = useTheme()
  const leafNodes = useMemo(() => getLeafNodes(template?.variables ?? {}), [template?.variables])

  const [apiMode, setApiMode] = useState("test")
  const [templateData, setTemplateData] = useState(null)
  const [catalogMap, setCatalogMap] = useState({})
  const [editableIssueVars, setEditableIssueVars] = useState([])
  const [variables, setVariables] = useState({})
  const [uploadRows, setUploadRows] = useState([])
  const [passesToProcess, setPassesToProcess] = useState(0)
  const [makePassResults, setMakePassResults] = useState([])
  const [makePassProgress, setMakePassProgress] = useState(null)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)
  const [resultsExportDialogOpen, setResultsExportDialogOpen] = useState(false)
  const [correctedExportDialogOpen, setCorrectedExportDialogOpen] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})
  const [testResult, setTestResult] = useState(null)
  const [testResultOpen, setTestResultOpen] = useState(false)
  const [liveRowDialog, setLiveRowDialog] = useState(null)
  const [liveDuplicateDecision, setLiveDuplicateDecision] = useState("fix")
  const [liveRowViewMode, setLiveRowViewMode] = useState(() => {
    try {
      return window.localStorage.getItem('makePasses.liveRowViewMode') || 'list'
    } catch {
      return 'list'
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem('makePasses.liveRowViewMode', liveRowViewMode)
    } catch {
      // ignore localStorage errors
    }
  }, [liveRowViewMode])

  useEffect(() => {
    const loadTemplate = async () => {
      const result = await getTemplateData({ apiSource, apiKey, templateId: template.template_id })
      if (!result?.ok || !result?.template) return

      setTemplateData(result.template)
      setVariables(_.cloneDeep(result.template.variables ?? {}))

      const map = _.keyBy(result.template.variable_catalog ?? [], "var_name")
      setCatalogMap(map)

      // Show only variables that actually exist in this template's defaults,
      // then enrich with catalog metadata (type/required flags) when available.
      const templateLeafPaths = getLeafNodes(result.template.variables ?? {})
      const editable = _.sortBy(
        templateLeafPaths.map(varName => ({
          var_name: varName,
          var_type: map[varName]?.var_type,
          readonly_managed: map[varName]?.readonly_managed
        }))
          .filter(item => isIssueEditable(item, Number(result.template?.managed_pass_template) === 1)),
        ["var_name"]
      )
      setEditableIssueVars(editable)
    }

    if (template?.template_id) loadTemplate()
  }, [apiKey, apiSource, template?.template_id])

  const coerceByCatalog = input => {
    const next = _.cloneDeep(input)
    Object.keys(catalogMap).forEach(path => {
      const varType = catalogMap[path]?.var_type
      const current = _.get(next, path)
      if (current === null || current === undefined) return

      if (varType === "boolean" && typeof current === "string") {
        const lower = current.trim().toLowerCase()
        if (lower === "true") _.set(next, path, true)
        if (lower === "false") _.set(next, path, false)
      }

      if (["integer", "number", "money"].includes(varType) && typeof current === "string") {
        const trimmed = current.trim()
        if (trimmed !== "" && !Number.isNaN(Number(trimmed))) {
          _.set(next, path, varType === "integer" ? Number.parseInt(trimmed, 10) : Number(trimmed))
        }
      }
    })
    return next
  }

  const setFieldValue = (path, nextValue) => setVariables(current => {
    const next = _.cloneDeep(current)
    _.set(next, path, nextValue)
    return next
  })

  const handleTestUpload = async file => {
    const reader = new FileReader()
    reader.onloadend = () => {
      try {
        const base64Data = reader.result.replace(/[^,]+base64,/, '')
        const parsedData = parseUploadedData({ base64Data, file, variables: templateData?.variables ?? template.variables })
        const firstRow = Array.isArray(parsedData) ? parsedData[0] : null
        if (!firstRow) return enqueueSnackbar({ message: 'Uploaded file is empty', variant: 'warning' })
        setVariables(coerceByCatalog(_.merge({}, _.cloneDeep(templateData?.variables ?? template.variables), firstRow)))
        enqueueSnackbar({ message: 'Variables loaded from upload', variant: 'success' })
      }
      catch (err) {
        enqueueSnackbar({ message: err?.message ?? 'Failed to parse file', variant: 'error' })
      }
    }
    reader.readAsDataURL(file)
  }

  const handleLiveUpload = async file => {
    const reader = new FileReader()
    reader.onloadend = () => {
      try {
        const base64Data = reader.result.replace(/[^,]+base64,/, '')
        const parsedData = parseUploadedData({
          base64Data,
          file,
          variables: templateData?.variables ?? template.variables,
          requireJsonArray: file.name.toLowerCase().endsWith('.json')
        }) ?? []

        if (!Array.isArray(parsedData) || !parsedData.length) {
          return enqueueSnackbar({ message: 'Upload must contain at least one row', variant: 'warning' })
        }

        if (parsedData.length > 100) {
          return enqueueSnackbar({ message: 'Live upload is limited to 100 passes', variant: 'error' })
        }

        setUploadRows(parsedData.map(row => coerceByCatalog(row)))
        enqueueSnackbar({ message: `Loaded ${parsedData.length} row(s)`, variant: 'success' })
      }
      catch (err) {
        enqueueSnackbar({ message: err?.message ?? 'Failed to parse upload', variant: 'error' })
      }
    }
    reader.readAsDataURL(file)
  }

  const createSingleTestPass = async () => {
    setProcessing(true)
    try {
      const payload = buildIssueVariablesPayload({
        source: coerceByCatalog(variables),
        templateDefaults: templateData?.variables ?? template.variables ?? {},
        catalogMap,
        isManagedPassTemplate: Number(templateData?.managed_pass_template) === 1,
        includeDuplicates: false
      })
      const result = await makePass({
        apiKey,
        apiSource,
        apiMode: "test",
        templateId: template.template_id,
        variables: payload
      })

      if (!result?.ok) {
        setMakePassError(result?.error ?? { message: "Failed to create pass" })
        setErrorDialogOpen(true)
        return
      }

      setTestResult({
        id: result.pass_id,
        appleUrl: result.appleUrl,
        googleUrl: result.googleUrl,
      })
      setTestResultOpen(true)
    }
    finally {
      setProcessing(false)
    }
  }

  const createLivePasses = async () => {
    if (!uploadRows.length) return
    if (duplicateSummary.totalDuplicateValues > 0 && liveDuplicateDecision === "pending") {
      enqueueSnackbar({ message: "Choose how to handle values matching template defaults before creating passes.", variant: "warning" })
      return
    }

    setProcessing(true)
    setMakePassResults([])
    setPassesToProcess(0)
    setMakePassProgress(null)
    const dataToSend = liveDuplicateDecision === "force"
      ? uploadRows
      : correctedUploadRows

    const response = await batchSendMakePasses({
      apiKey,
      apiSource,
      apiMode: "live",
      dataSource: "upload",
      passData: dataToSend,
      templateId: template.template_id,
      variables: templateData?.variables ?? template.variables,
      setPassesToProcess,
      setMakePassResults,
      batchSize: 10,
      onProgress: setMakePassProgress
    })

    if (!response?.ok) {
      setMakePassError(response.error)
      setErrorDialogOpen(true)
    }
  }

  const livePreviewRows = useMemo(() => uploadRows.map((row, idx) => {
    const flat = { __row_id: idx + 1 }
    leafNodes.forEach(path => { flat[path] = _.get(row, path) })
    return flat
  }), [uploadRows, leafNodes])

  const liveColumns = useMemo(() => {
    return [
      { accessorKey: '__row_id', header: '#', size: 70 },
      ...leafNodes.map(path => ({
        accessorKey: path,
        header: path,
        size: 220,
      }))
    ]
  }, [leafNodes])

  const liveTable = useMaterialReactTable({
    columns: liveColumns,
    data: livePreviewRows,
    enablePagination: true,
    enableRowSelection: false,
    enableDensityToggle: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    positionToolbarAlertBanner: 'none',
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => setLiveRowDialog(uploadRows[row.index]),
      sx: { cursor: 'pointer' }
    }),
  })

  const testExportData = useMemo(() => [variables], [variables])
  const correctedUploadRows = useMemo(() => {
    return uploadRows.map(row => buildIssueVariablesPayload({
      source: row,
      templateDefaults: templateData?.variables ?? template.variables ?? {},
      catalogMap,
      isManagedPassTemplate: Number(templateData?.managed_pass_template) === 1,
      includeDuplicates: false
    }))
  }, [catalogMap, template.variables, templateData?.managed_pass_template, templateData?.variables, uploadRows])

  const duplicateSummary = useMemo(() => {
    const templateDefaults = templateData?.variables ?? template.variables ?? {}
    const isManaged = Number(templateData?.managed_pass_template) === 1
    const paths = _.uniq([...getLeafNodes(templateDefaults), ...uploadRows.flatMap(row => getLeafNodes(row))])

    let rowsWithDuplicates = 0
    let totalDuplicateValues = 0
    uploadRows.forEach(row => {
      let rowHasDuplicate = false
      paths.forEach(path => {
        if (!_.has(row, path)) return
        const required = isRequiredVariableValueForTemplate({ catalogEntry: catalogMap[path], isManagedPassTemplate: isManaged })
        if (required) return
        if (_.has(templateDefaults, path) && _.isEqual(_.get(row, path), _.get(templateDefaults, path))) {
          totalDuplicateValues += 1
          rowHasDuplicate = true
        }
      })
      if (rowHasDuplicate) rowsWithDuplicates += 1
    })

    return { rowsWithDuplicates, totalDuplicateValues }
  }, [catalogMap, template.variables, templateData?.managed_pass_template, templateData?.variables, uploadRows])

  useEffect(() => {
    if (!uploadRows.length) {
      setLiveDuplicateDecision("fix")
      return
    }

    setLiveDuplicateDecision(duplicateSummary.totalDuplicateValues > 0 ? "pending" : "fix")
  }, [duplicateSummary.totalDuplicateValues, uploadRows.length])

  const resultsComplete = makePassResults.length > 0 && makePassResults.length === passesToProcess
  const uploadDropTarget = useMemo(() => (
    <UploadDropTarget
      textColor={theme.palette.text.primary}
      mutedColor={theme.palette.text.secondary}
      borderColor={theme.palette.divider}
      backgroundColor={theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)"}
    />
  ), [theme.palette.divider, theme.palette.mode, theme.palette.text.primary, theme.palette.text.secondary])

  if (!templateData) return null

  if (apiMode === "live" && processing) {
    return (
      <div className='h-full'>
        <ResultsGrid
          template={template}
          passesToProcess={passesToProcess}
          makePassResults={makePassResults}
          setMakePassResults={setMakePassResults}
          setProcessing={setProcessing}
          progress={makePassProgress}
        />
        <div className='flex justify-end mt-3 mr-3'>
          <Button
            variant='outlined'
            startIcon={<SaveAltIcon />}
            onClick={() => setResultsExportDialogOpen(true)}
            disabled={!resultsComplete}
          >
            Download results
          </Button>
        </div>
        <DataExportDialog
          open={resultsExportDialogOpen}
          setOpen={setResultsExportDialogOpen}
          fileName={`${template.template_id}-new-passes`}
          data={makePassResults}
          excelExport={excelExport}
          csvExport={csvExport}
        />
        <ActionErrorDialog title="Create passes error" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
      </div>
    )
  }

  return (
    <>
      <DataExportDialog
        open={dataExportDialogOpen}
        setOpen={setDataExportDialogOpen}
        fileName={`${template.template_id}-default-variables`}
        data={testExportData}
        excelExport={excelExport}
        csvExport={csvExport}
      />
      <DataExportDialog
        open={correctedExportDialogOpen}
        setOpen={setCorrectedExportDialogOpen}
        fileName={`${template.template_id}-corrected-live-data`}
        data={correctedUploadRows}
        excelExport={excelExport}
        csvExport={csvExport}
      />

      <div className='h-full min-h-0 w-full mt-3 flex flex-col gap-4'>
        <StyledPaper sx={{ p: 2 }}>
          <div className='flex flex-row items-center justify-between gap-4 flex-wrap'>
            <ToggleButtonGroup value={apiMode} exclusive onChange={(_, mode) => mode && setApiMode(mode)}>
              <ToggleButton value="test">TEST Pass</ToggleButton>
              <ToggleButton value="live">LIVE Passes</ToggleButton>
            </ToggleButtonGroup>
            {apiMode === "test" ? (
              <div className='flex gap-2'>
                <Button variant='outlined' onClick={() => setDataExportDialogOpen(true)} startIcon={<SaveAltIcon />}>Download data template</Button>
                <FileUploader
                  handleChange={handleTestUpload}
                  types={["json", "csv", "xlsx"]}
                  name="testVarsUpload"
                  children={uploadDropTarget}
                  dropMessageStyle={{ color: theme.palette.text.primary }}
                />
              </div>
            ) : (
              <FileUploader
                handleChange={handleLiveUpload}
                types={["json", "csv", "xlsx"]}
                name="liveUpload"
                children={uploadDropTarget}
                dropMessageStyle={{ color: theme.palette.text.primary }}
              />
            )}
          </div>
        </StyledPaper>

        {apiMode === "test" ? (
          <StyledPaper sx={{ flex: 1, minHeight: 0 }}>
            <div className='flex flex-col h-full min-h-0'>
              <Typography variant='h5' sx={{ mb: 2 }}>Editable Variables</Typography>
              <div className='flex-1 overflow-auto pr-2'>
                {editableIssueVars.map(item => (
                  <div key={item.var_name} className='grid grid-cols-[minmax(260px,1fr)_minmax(420px,2fr)] gap-3 items-center mb-2'>
                    <div className='font-semibold'>{item.var_name}</div>
                    <VariableInput
                      value={_.get(variables, item.var_name)}
                      templateDefaultValue={_.get(templateData.variables, item.var_name)}
                      varType={item.var_type}
                      required={isRequiredVariableValueForTemplate({
                        catalogEntry: item,
                        isManagedPassTemplate: Number(templateData?.managed_pass_template) === 1
                      })}
                      label={item.var_name}
                      onChange={value => setFieldValue(item.var_name, value)}
                    />
                  </div>
                ))}
                {!editableIssueVars.length && <div>No variables found in this template.</div>}
              </div>
              <div className='flex justify-end mt-3'>
                <Button variant='contained' onClick={createSingleTestPass} disabled={processing}>Create TEST pass</Button>
              </div>
            </div>
          </StyledPaper>
        ) : (
          <StyledPaper sx={{ flex: 1, minHeight: 0 }}>
            <div className='flex flex-col h-full min-h-0'>
              <div className='flex flex-row justify-between items-center mb-2'>
                <Typography variant='h5'>Upload Review</Typography>
                <div>
                  <b>{uploadRows.length}</b> / 100 rows loaded
                </div>
              </div>
              {duplicateSummary.totalDuplicateValues > 0 && (
                <Alert severity='warning' sx={{ mb: 2 }}>
                  <div className='flex flex-col gap-2'>
                    <div>
                      Found <b>{duplicateSummary.totalDuplicateValues}</b> uploaded values (across <b>{duplicateSummary.rowsWithDuplicates}</b> row(s)) that match template defaults.
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => setLiveDuplicateDecision("force")}
                      >
                        Accept and force include all values
                      </Button>
                      <Button
                        size='small'
                        variant='contained'
                        onClick={() => {
                          setUploadRows(correctedUploadRows)
                          setLiveDuplicateDecision("fix")
                          enqueueSnackbar({ message: "Duplicate default values removed (required values retained).", variant: "success" })
                        }}
                      >
                        Fix and remove duplicates
                      </Button>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => setCorrectedExportDialogOpen(true)}
                      >
                        Download corrected data
                      </Button>
                    </div>
                  </div>
                </Alert>
              )}
              <div className='flex-1 min-h-0'>
                <MaterialReactTable table={liveTable} />
              </div>
              <div className='flex justify-end mt-3'>
                <Button
                  variant='contained'
                  onClick={createLivePasses}
                  disabled={!uploadRows.length || processing || (duplicateSummary.totalDuplicateValues > 0 && liveDuplicateDecision === "pending")}
                >
                  Make passes
                </Button>
              </div>
            </div>
          </StyledPaper>
        )}
      </div>

      <Dialog
        open={testResultOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return
          setTestResultOpen(false)
        }}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Pass created</DialogTitle>
        <DialogContent>
          <div className='mb-4'>
            Pass ID: <ClickToCopy source={testResult?.id}>{testResult?.id}</ClickToCopy>
          </div>
          <div className='flex flex-row gap-5 justify-between'>
            <InstallPanel url={testResult?.appleUrl} buttonImage={appleButton} label='Apple URL' />
            <InstallPanel url={testResult?.googleUrl} buttonImage={googleButton} label='Google URL' />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={() => setTestResultOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(liveRowDialog)} onClose={() => setLiveRowDialog(null)} maxWidth='md' fullWidth>
        <DialogTitle>Uploaded row</DialogTitle>
        <DialogContent>
          <div className='flex justify-end mb-2'>
            <ToggleButtonGroup size='small' value={liveRowViewMode} exclusive onChange={(_, mode) => mode && setLiveRowViewMode(mode)}>
              <ToggleButton value='list'>List</ToggleButton>
              <ToggleButton value='json'>JSON</ToggleButton>
            </ToggleButtonGroup>
          </div>
          {liveRowViewMode === 'json' ? (
            <pre className='p-3 rounded bg-black/20 overflow-auto max-h-[50vh]'>{JSON.stringify(liveRowDialog, null, 2)}</pre>
          ) : (
            <div className='grid grid-cols-[minmax(260px,1fr)_minmax(260px,2fr)] gap-2'>
              {leafNodes.map(path => (
                <div key={path} className='contents'>
                  <div className='font-semibold'>{path}</div>
                  <div>{String(_.get(liveRowDialog, path) ?? "")}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setLiveRowDialog(null)} startIcon={<CloseIcon />}>Close</Button>
        </DialogActions>
      </Dialog>

      <ActionErrorDialog title="Create passes error" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
    </>
  )
}


const shortUuidDictionary = [
  '0', '1', '2', '3',
  '4', '5', '6', '7',
  '8', '9', 'A', 'B',
  'C', 'D', 'E', 'F',
  'G', 'H', 'J', 'K',
  'L', 'M', 'N', 'P', 'Q',
  'R', 'T', 'U', 'V', 'W',
  'X', 'Y'
]

const shortUUID = () => {
  const uid = new ShortUniqueId({ length: 12, dictionary: shortUuidDictionary })
  const uuid = uid.rnd()
  const uuidArray = uuid.match(/.{1,4}/g)
  return uuidArray.join("-")
}

const isIntegerInput = input => /^-?\d*$/.test(input)
const isDecimalInput = input => /^-?\d*(\.\d*)?$/.test(input)
const isValidColourInput = input => /^#?[0-9a-fA-F]{6}$/.test(input ?? "")
const normaliseColourInput = input => /^#[0-9a-fA-F]{6}$/.test(input ?? "")
  ? input
  : /^[0-9a-fA-F]{6}$/.test(input ?? "")
    ? `#${input}`
    : null
const isValidCurrencyCodeInput = input => /^[A-Z]{3}$/.test(input ?? "")
  && currencies.some(currency => currency.code === input)
const isColourSubType = varSubType => ["colour", "color", "colours", "colors"].includes(varSubType)
const isValidDateInput = input => /^\d{8}$/.test(input ?? "") && moment(input, "YYYYMMDD", true).isValid()
const isValidDateTimeInput = input => /^\d{14}$/.test(input ?? "") && moment(input, "YYYYMMDDHHmmss", true).isValid()

const validateValueInput = ({ input, varSubType, required = false, label = "Value" }) => {
  const value = (input ?? "").toString()
  if (required && value.trim() === "") return { valid: false, helperText: `${label} is required.` }
  if (!required && value.trim() === "") return { valid: true, helperText: "" }

  switch (varSubType) {
    case "email":
      return { valid: isEmail(value), helperText: isEmail(value) ? "" : "Enter a valid email address." }
    case "www":
    case "link":
      return {
        valid: isURL(value, { protocols: ['http', 'https'], require_protocol: true }),
        helperText: isURL(value, { protocols: ['http', 'https'], require_protocol: true }) ? "" : "Enter a valid URL starting with http:// or https://."
      }
    case "number":
    case "money":
      return { valid: isDecimalInput(value), helperText: isDecimalInput(value) ? "" : "Enter a valid number." }
    case "integer":
      return { valid: isIntegerInput(value), helperText: isIntegerInput(value) ? "" : "Enter a valid integer." }
    case "date":
      return { valid: isValidDateInput(value), helperText: isValidDateInput(value) ? "" : "Use YYYYMMDD and enter a valid date." }
    case "datetime":
      return { valid: isValidDateTimeInput(value), helperText: isValidDateTimeInput(value) ? "" : "Use YYYYMMDDHHmmss and a valid 24-hour time." }
    case "colour":
    case "color":
    case "colours":
    case "colors":
      return { valid: isValidColourInput(value), helperText: isValidColourInput(value) ? "" : "Use a 6-digit hex colour such as #A1B2C3." }
    case "choice":
      return { valid: isValidCurrencyCodeInput(value), helperText: isValidCurrencyCodeInput(value) ? "" : "Use a valid 3-letter ISO currency code such as GBP." }
    default:
      return { valid: true, helperText: "" }
  }
}

const isIssueEditable = (item, isManagedPassTemplate = false) => {
  if (!item) return true
  if (!isManagedPassTemplate) return true
  return Number(item.readonly_managed) !== 1
}

const VariableInput = ({ value, templateDefaultValue, varType, onChange, required = false, label = "Value" }) => {
  const stringValue = value?.toString?.() ?? ""
  const validation = validateValueInput({ input: stringValue, varSubType: varType, required, label })
  const matchesTemplateDefault = _.isEqual(value, templateDefaultValue)
  const helperText = !validation.valid
    ? validation.helperText
    : matchesTemplateDefault
      ? "Matches template default (will not be overridden)."
      : ""
  const defaultValueFieldSx = matchesTemplateDefault
    ? { "& .MuiInputBase-root": { opacity: 0.75, backgroundColor: "action.hover" } }
    : undefined

  if (varType === "boolean") {
    return (
      <TextField
        select
        size='small'
        fullWidth
        value={stringValue === "" ? "false" : stringValue.toLowerCase()}
        onChange={event => onChange(event.target.value === "true")}
        error={!validation.valid}
        helperText={helperText}
        sx={defaultValueFieldSx}
      >
        <MenuItem value="false">false</MenuItem>
        <MenuItem value="true">true</MenuItem>
      </TextField>
    )
  }

  if (isColourSubType(varType)) {
    const pickerColour = normaliseColourInput(stringValue) ?? "#ffffff"

    return (
      <div className='flex flex-col gap-2'>
        <TextField
          size='small'
          fullWidth
          value={stringValue}
          onChange={event => onChange(event.target.value)}
          error={!validation.valid}
          helperText={helperText}
          sx={defaultValueFieldSx}
        />
        <HexColorPicker color={pickerColour} onChange={onChange} />
      </div>
    )
  }

  if (varType === "choice") {
    return (
      <TextField
        select
        size='small'
        fullWidth
        value={stringValue}
        onChange={event => onChange(event.target.value)}
        error={!validation.valid}
        helperText={helperText}
        sx={defaultValueFieldSx}
      >
        <MenuItem value="">Select currency</MenuItem>
        {currencies.map(currency => (
          <MenuItem key={currency.code} value={currency.code}>
            {currency.code} - {currency.name}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  if (["integer", "number", "money"].includes(varType)) {
    return (
      <TextField
        size='small'
        fullWidth
        value={stringValue}
        onChange={event => {
          if (varType === "integer" && !isIntegerInput(event.target.value)) return
          if (["number", "money"].includes(varType) && !isDecimalInput(event.target.value)) return
          onChange(event.target.value)
        }}
        inputProps={{ inputMode: 'decimal' }}
        error={!validation.valid}
        helperText={helperText}
        sx={defaultValueFieldSx}
      />
    )
  }

  return (
    <TextField
      size='small'
      fullWidth
      value={stringValue}
      onChange={event => onChange(event.target.value)}
      error={!validation.valid}
      helperText={helperText}
      sx={defaultValueFieldSx}
    />
  )
}

const unsetPathAndPrune = (obj, path) => {
  _.unset(obj, path)
  const parts = path.split(".")
  for (let i = parts.length - 1; i > 0; i -= 1) {
    const parentPath = parts.slice(0, i).join(".")
    const parentValue = _.get(obj, parentPath)
    if (_.isPlainObject(parentValue) && Object.keys(parentValue).length === 0) _.unset(obj, parentPath)
    else break
  }
}

const buildIssueVariablesPayload = ({ source, templateDefaults, catalogMap, isManagedPassTemplate, includeDuplicates = false }) => {
  const next = _.cloneDeep(source ?? {})
  const paths = _.uniq([...getLeafNodes(templateDefaults ?? {}), ...getLeafNodes(next)])

  paths.forEach(path => {
    if (!_.has(next, path)) return
    const sameAsDefault = _.has(templateDefaults, path) && _.isEqual(_.get(next, path), _.get(templateDefaults, path))

    if (!includeDuplicates && sameAsDefault) {
      unsetPathAndPrune(next, path)
    }
  })

  return next
}

const InstallPanel = ({ url, buttonImage, label }) => {
  return (
    <div className='flex flex-col gap-4 items-center'>
      <div><Link to={url} target='_pass'><img src={buttonImage} width={250} /></Link></div>
      <div className='bg-white p-4 rounded'>
        <QRCode value={url} />
      </div>
      <div>
        <ClickToCopy source={url ?? ""} successMessage={`${label} copied`}>
          Copy {label}
        </ClickToCopy>
      </div>
    </div>
  )
}

const UploadDropTarget = ({ textColor, mutedColor, borderColor, backgroundColor }) => {
  return (
    <div
      style={{
        borderRadius: 10,
        border: `2px dashed ${borderColor}`,
        backgroundColor,
        color: textColor,
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 12,
        paddingBottom: 12,
        minWidth: 320
      }}
    >
      <div className='flex flex-row items-center gap-3'>
        <UploadFileOutlinedIcon />
        <div>
          <div className='text-base font-semibold'>Upload data file</div>
          <div style={{ color: mutedColor, fontSize: 13 }}>Click to choose a file, or drag one here</div>
          <div style={{ color: mutedColor, fontSize: 12 }}>[json, csv, xlsx]</div>
        </div>
      </div>
    </div>
  )
}


const barcodeOptions = {
  bcid: 'qrcode',
  scale: 2,
  includetext: false,
  eclevel: "M",
  ecaddchars: 3,
  layers: 8,
  width: 60,
  height: 60,
  padding: 8,
  backgroundcolor: "#ffffff"
}


const StampcardSignup = ({ template }) => {

  const { apiSource, apiKey, setProcessing, client } = useContext(AppContext)

  const [stampCard, setStampCard] = useState()
  const [barcode, setBarcode] = useState(null)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})

  useEffect(() => {
    let canvas = document.createElement('canvas')

    BwipJs.toCanvas(canvas, { ...barcodeOptions, text: `https://signup.smartix.uk/stamp/${client?.id}/${template.template_id}` })

    let barcode = canvas.toDataURL('image/svg')

    setBarcode(barcode)
  }, [])

  const makeTestStampcard = async () => {
    setStampCard(null)
    setProcessing(true)
    const sc = await createTestStampcard({ apiSource, apiKey, apiMode: "test", templateId: template.template_id })
    setProcessing(false)

    if (sc.ok === false) {
      setMakePassError(sc.error)
      setErrorDialogOpen(true)
    } else {
      setStampCard(sc)
    }
  }

  const gotoSignupForm = () => {
    window.open(`https://signup.smartix.uk/${template.client_id}/${template.template_id}`)
  }

  return (
    <>
      <div className='m-10 flex flex-col gap-10'>
        <div className='m-10 flex flex-row gap-10 justify-around'>

          <StyledPaper>
            <div className='flex flex-col gap-10 p-5 items-center'>
              {!stampCard &&
                <>
                  <div className='mt-40'>You can create a test stampcard here.</div>
                  <div><Button variant="contained" onClick={makeTestStampcard}>Create a TEST Stampcard</Button></div>
                </>
              }

              {stampCard &&
                <>
                  <Typography variant='h4'>
                    <CopyToClipboard text={stampCard.pass_id} onCopy={() => enqueueSnackbar({ message: 'Pass ID copied', variant: 'success' })}>
                      <div className='text-nowrap  cursor-pointer'><ContentCopyIcon color='inherit' className="mr-5" />New Stampcard ID: {stampCard.pass_id}</div>
                    </CopyToClipboard>
                  </Typography>
                  <div className='flex gap-40 items-center m-10'>

                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={stampCard.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={stampCard.appleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={stampCard?.appleUrl} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>

                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={stampCard.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={stampCard.googleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={stampCard?.googleUrl} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>

                  </div>
                  <div className='flex flex-row justify-end w-full'>
                    <Button variant='outlined' onClick={() => setStampCard(null)}>close</Button>
                  </div>
                </>
              }
            </div>
          </StyledPaper>

          {!stampCard &&
            <StyledPaper>
              <div className='flex flex-col gap-3 items-center p-5'>
                <div>If you wish to create live stampcard, then you must use the stampcard signup form </div>
                <Button variant="contained" onClick={gotoSignupForm}>Go to live signup form</Button>

                <img src={barcode} className="mt-5" />
                <div>Scan for link to signup form</div>
                <div><a target="_blank" href={`https://signup.smartix.uk/${template.client_id}/${template.template_id}`}>https://signup.smartix.uk/{template.client_id}/{template.template_id}</a></div>
              </div>
            </StyledPaper>
          }
        </div>
      </div>

      <ActionErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />

    </>
  )
}


const LoyaltycardSignup = ({ template }) => {

  const { apiSource, apiKey, setProcessing, client } = useContext(AppContext)

  const [loyaltyCard, setLoyaltyCard] = useState()
  const [barcode, setBarcode] = useState(null)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})

  useEffect(() => {
    let canvas = document.createElement('canvas')

    BwipJs.toCanvas(canvas, { ...barcodeOptions, text: `https://signup.smartix.uk/loyalty/${client?.id}/${template.template_id}` })

    let barcode = canvas.toDataURL('image/svg')

    setBarcode(barcode)
  }, [])

  const makeTestLoyaltycard = async () => {
    setLoyaltyCard(null)
    setProcessing(true)
    const sc = await createTestLoyaltycard({ apiSource, apiKey, apiMode: "test", templateId: template.template_id })
    setProcessing(false)

    if (sc.ok === false) {
      setMakePassError(sc.error)
      setErrorDialogOpen(true)
    } else {
      setLoyaltyCard(sc)
    }
  }

  const gotoSignupForm = () => {
    window.open(`https://signup.smartix.uk/loyalty/${template.client_id}/${template.template_id}`)
  }

  return (
    <>
      <div className='m-10 flex flex-col gap-10'>
        <div className='m-10 flex flex-row gap-10 justify-around'>

          <StyledPaper>
            <div className='flex flex-col gap-10 p-5 items-center'>
              {!loyaltyCard &&
                <>
                  <div className='mt-40'>You can create a test loyalty card here.</div>
                  <div><Button variant="contained" onClick={makeTestLoyaltycard}>Create a TEST Loyalty card</Button></div>
                </>
              }

              {loyaltyCard &&
                <>
                  <Typography variant='h4'>
                    <CopyToClipboard text={loyaltyCard.pass_id} onCopy={() => enqueueSnackbar({ message: 'Pass ID copied', variant: 'success' })}>
                      <div className='text-nowrap  cursor-pointer'><ContentCopyIcon color='inherit' className="mr-5" />New Loyalty card ID: {loyaltyCard.pass_id}</div>
                    </CopyToClipboard>
                  </Typography>
                  <div className='flex gap-40 items-center m-10'>


                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={loyaltyCard.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={loyaltyCard.appleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={loyaltyCard?.appleUrl} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>

                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={loyaltyCard.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={loyaltyCard.googleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={loyaltyCard?.googleUrl} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>
                  </div>
                  <div className='flex flex-row justify-end w-full'>
                    <Button variant='outlined' onClick={() => setLoyaltyCard(null)}>close</Button>
                  </div>
                </>
              }
            </div>
          </StyledPaper>

          {!loyaltyCard &&
            <StyledPaper>
              <div className='flex flex-col gap-3 items-center p-5'>
                <div>If you wish to create live loyalty card, then please use the loyalty card signup form: </div>
                <Button variant="contained" onClick={gotoSignupForm}>Go to live signup form</Button>

                <img src={barcode} className="mt-5" />
                <div>Scan for link to signup form</div>
                <div><a target="_blank" href={`https://signup.smartix.uk/loyalty/${template.client_id}/${template.template_id}`}>https://signup.smartix.uk/{template.client_id}/{template.template_id}</a></div>
              </div>
            </StyledPaper>
          }
        </div>
      </div>

      <ActionErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />

    </>
  )
}



const Giftcard = ({ template }) => {

  const { apiSource, apiKey, setProcessing,copyingEmail, setCopyingEmail } = useContext(AppContext)

  const [giftCard, setGiftCard] = useState(null)
  const [emailBarcodes, setEmailBarcodes] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})

  const navigate = useNavigate()

  const copyEmail = () => {
    setCopyingEmail(true)
  }

  const copyEmailBarcodes = () => {
    setEmailBarcodes(true)
    setCopyingEmail(true)
  }

  useEffect(() => {
    if (copyingEmail) {
      document.execCommand("SelectAll", true)
      document.execCommand("Copy", true)
      document.execCommand("UnSelect", true)

      setCopyingEmail(false)
      setEmailBarcodes(false)
    }
  }, [copyingEmail])

  const makeTestGiftcard = async () => {
    setGiftCard(null)
    setProcessing(true)
    const gc = await createGiftcard({ apiSource, apiKey, apiMode: "test", templateId: template.template_id })
    setProcessing(false)

    if (gc.ok === false) {
      setMakePassError(gc.error)
      setErrorDialogOpen(true)
    } else {
      setGiftCard(gc)
    }
  }

  if (copyingEmail) return (
    <div style={{ backgroundColor: "#fff", width: "100%" }}>
      <br />
      <table cellPadding={20} cellSpacing={20}>
        <tbody>
          <tr style={{ height: 100 }}>
            <td><Link to={giftCard?.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link></td>
            <td><Link to={giftCard?.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link></td>
          </tr>
          {emailBarcodes &&
            <tr style={{ height: 200, width: 200 }}>
              <td >
                <div className='bg-white p-5'>
                  <QRCode
                    size={256}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    value={giftCard?.appleUrl}
                    viewBox={`0 0 256 256`}
                  />
                </div>
              </td>

              <td >
                <div className='bg-white p-5'>
                  <QRCode
                    size={256}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    value={giftCard?.googleUrl}
                    viewBox={`0 0 256 256`} />
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
      <br /><br />
    </div>
  )

  return (
    <>
      <div className='m-10 flex flex-col gap-10 h-full overflow-y-auto'>
        {!giftCard &&
          <div className='m-10 flex flex-row gap-10 justify-around'>

            <StyledPaper>
              <div className='flex flex-col gap-3 p-5'>
                <div className='mt-28'>You can create a test giftcard here.</div>
                <Button variant="contained" onClick={makeTestGiftcard}>Create a TEST Giftcard</Button>
              </div>
            </StyledPaper>

            <StyledPaper>
              <div className='flex flex-col gap-3 p-5'>
                <div className='flex flex-col gap-3 items-start'><Typography variant="h5" sx={{ p: 2 }}>Create a Giftcard</Typography></div>
                <CreateLiveGiftcardForm template={template} setGiftCard={setGiftCard} />
              </div>
            </StyledPaper>

          </div>
        }

        {giftCard &&
          <div className='flex flex-row gap-40 items-center m-10'>

            <StyledPaper>
              <div className='flex flex-row gap-20 p-5'>
                <div className='flex flex-col gap-10 items-start'>
                  <div style={{ height: 100 }} className="flex flex-col justify-around">
                    <Link to={giftCard.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link>
                  </div>

                  <div style={{ height: 250, width: 250 }} className='bg-white p-5' >
                    <QRCode
                      size={256}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      value={giftCard.appleUrl}
                      viewBox={`0 0 256 256`}
                    />
                  </div>

                  <CopyToClipboard text={giftCard?.appleUrl} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
                    <Button><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</Button>
                  </CopyToClipboard>
                </div>

                <div className='flex flex-col gap-10 items-start'>
                  <div style={{ height: 100 }} className="flex flex-col justify-around">
                    <Link to={giftCard.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link>
                  </div>

                  <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                    <QRCode
                      size={256}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      value={giftCard.googleUrl}
                      viewBox={`0 0 256 256`}
                    />
                  </div>

                  <CopyToClipboard text={giftCard?.googleUrl} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
                    <Button><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</Button>
                  </CopyToClipboard>
                </div>
              </div>
            </StyledPaper>

            <StyledPaper>
              <div className='flex flex-col gap-5 p-5'>
                <Typography variant='h6'>Gift card ID:
                  <CopyToClipboard text={giftCard.pass_id} onCopy={() => enqueueSnackbar({ message: 'Gift card ID copied', variant: 'success' })} options={{ format: 'text/plain' }}>
                    <Button ><ContentCopyIcon color='inherit' className="mr-5" />{giftCard.pass_id}</Button>
                  </CopyToClipboard>
                </Typography>

                <Button variant='contained' color="secondary" onClick={copyEmailBarcodes}><ContentCopyIcon color='inherit' className="mr-5" />Copy email content (with barcodes)</Button>
                <Button variant='contained' color="secondary" onClick={copyEmail}><ContentCopyIcon color='inherit' className="mr-5" />Copy email content (no barcodes)</Button>

                <Button variant='contained' color="secondary" onClick={() => navigate("../passes/" + giftCard.pass_id)}>Goto pass details</Button>
                <br />
                <Button variant='contained' onClick={() => setGiftCard(null)}>Close</Button>
              </div>
            </StyledPaper>

          </div>
        }

      </div>

      <ActionErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
    </>
  )
}


const CreateLiveGiftcardForm = ({ template, setGiftCard }) => {
  const { apiSource, apiKey, setProcessing } = useContext(AppContext)

  const defaultExpiry = moment().add(1, "year")

  const [amount, setAmount] = useState("")
  const [hasExpiry, setHasExpiry] = useState("yes")
  const [expiry, setExpiry] = useState(defaultExpiry)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState()

  const regex = /^(\d+(?:[\.]\d{0,2})?)$/i

  const makeGiftcard = async () => {
    setGiftCard(null)
    setProcessing(true)

    const gc = await createGiftcard({ apiSource, apiKey, apiMode: "live", templateId: template.template_id, amount: parseInt(amount, 10), expiry: hasExpiry === "yes" ? expiry.format("YYYYMMDDHHmmSS") : undefined })

    setProcessing(false)

    if (gc.ok === false) {
      setMakePassError(gc.error)
      setErrorDialogOpen(true)
    } else {
      setGiftCard(gc)
    }
  }

  return (
    <>
      <div>

        <TextField
          sx={{ m: 1, width: '25ch' }}
          value={amount}
          onChange={e => {
            if (e.target.value === '' || regex.test(e.target.value)) {
              setAmount(e.target.value)
            }
          }}
          variant='outlined'
          label="Amount"
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><div style={{ fontSize: 40, color: "#999" }}>£</div></InputAdornment>,
              style: { fontSize: 40, width: 235 }
            },
            inputLabel: {
              shrink: true
            }
          }}
        />

      </div>
      <RadioGroup
        row
        value={hasExpiry}
        onChange={e => setHasExpiry(e.target.value)}
      >
        <FormControlLabel value="yes" control={<Radio />} label="Set an expiry date" />
        <FormControlLabel value="no" control={<Radio />} label="No expiry date" />
      </RadioGroup>


      <div><DatePicker label="Expiry Date" value={expiry} onChange={date => setExpiry(date)} disabled={hasExpiry === "no"} /></div>

      <div><Button onClick={makeGiftcard} variant='contained' disabled={amount < 1} >Create Giftcard</Button></div>

      <ActionErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
    </>
  )
}
